// Pure helper for turning a CSS <length> string (as read off getComputedStyle
// for a custom property) into a FINITE pixel number.
//
// WHY THIS EXISTS — the "act as a member" one-post/no-scroll bug:
//   Unregistered CSS custom properties keep calc()/env() expressions
//   UNRESOLVED in their computed value. `--f7-safe-area-top` is a plain `0px`
//   normally, but while an operator is impersonating a member the
//   `html.acting-as-active` rule (src/css/app.css) redefines it to
//   `calc(env(safe-area-inset-top, 0px) + 44px)`. A naive
//   `parseInt("calc(...)")` returns NaN, and feeding NaN into tanstack
//   `@tanstack/vue-virtual`'s `scrollMargin` collapses the visible-range math
//   to a single item that cannot scroll (VirtualPostList.vue). This helper
//   resolves such expressions to real pixels and NEVER returns NaN.
//
// Fast path (plain numeric-leading length like "0px"/"44px") uses parseInt to
// mirror the original call sites exactly, so normal-mode output is unchanged.

/**
 * @param {string|number|null|undefined} value  Raw CSS length ("0px", "44px", "calc(...)", …).
 * @param {number} [fallback=0]                  Returned when nothing finite can be resolved.
 * @returns {number} finite pixel value
 */
export function cssLengthToPx(value, fallback = 0) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;

  const str = String(value).trim();
  if (str === '') return fallback;

  // Fast path: a plain numeric-leading length ("0px", "44px", "-5px").
  // parseInt(base 10) matches the original `parseInt(...)` call sites, so any
  // value that already worked keeps its exact result.
  if (/^[+-]?\d/.test(str)) {
    const direct = parseInt(str, 10);
    if (Number.isFinite(direct)) return direct;
  }

  // Slow path: an expression the CSS engine must lay out (calc()/env()/var()),
  // e.g. `--f7-safe-area-top` while impersonating. Ask the browser to compute a
  // real length on a hidden probe. Guarded so a headless/SSR context (or a DOM
  // that can't lay out calc) falls back to a finite number instead of NaN.
  if (typeof document !== 'undefined' && document.body) {
    let probe;
    try {
      probe = document.createElement('div');
      probe.style.cssText =
        `position:absolute;visibility:hidden;pointer-events:none;height:${str};`;
      document.body.appendChild(probe);
      const px = probe.getBoundingClientRect().height;
      if (Number.isFinite(px)) return px;
    } catch {
      /* fall through to fallback */
    } finally {
      if (probe && probe.parentNode) probe.parentNode.removeChild(probe);
    }
  }

  return fallback;
}
